import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import config from 'constants/config'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { dateDifference } from 'lib/datetime/dateUtils'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import PaletteSheet from 'LuxKit/PaletteSheet'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'

const FlightCardContainer = styled(Clickable)`
  display: grid;
  grid-template-rows: ${rem(200)} 1fr;
  position: relative;
  width: 100%;
  gap: ${rem(12)};
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
`

const CarrierContainer = styled.div`
  position: absolute;
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
  bottom: ${rem(12)};
  left: ${rem(8)};
`

const CarrierImage = styled(Image)`
  max-width: ${rem(80)};
`

const LabelContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
`

interface Props {
  flightDeal: App.FlightDeal
  hasMultipleAirlines: boolean
}

function FlightsDealsGridCard(props: Props) {
  const { flightDeal, hasMultipleAirlines } = props
  const intl = useIntl()
  const { fareTypes, fareCabins } = useFlightFareLabels()
  const fareTypeLabel = fareTypes.find(type => type.value === flightDeal.fareType)?.label
  const fareClassLabel = fareCabins.find(type => type.value === flightDeal.fareClass)?.label

  const endDateDifference = useMemo(() => {
    const difference = dateDifference(new Date(flightDeal.salesEndDate))

    if (difference.days <= 1) {
      return intl.formatMessage(
        {
          id: 'FP3I2B5EhK',
          defaultMessage: '{hours}H : {minutes}M : {seconds}S',
          description: 'Countdown timer showing hours, minutes and seconds remaining for a flight deal',
        },
        {
          hours: Math.max(difference.hours, 0),
          minutes: Math.max(difference.minutes, 0),
          seconds: Math.max(difference.seconds, 0),
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'Nh5edmv0Az',
        defaultMessage: '{days, plural, one {# DAY} other {# DAYS}}',
        description: 'Countdown timer showing days remaining for a flight deal',
      },
      { days: difference.days },
    )
  }, [flightDeal.salesEndDate, intl])

  const hasCarrierLogo = flightDeal.carrier.logo?.id || flightDeal.carrier.logo?.url

  return (
    <FlightCardContainer
      to={`/flight/deal/${flightDeal.id}`}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
    >
      {flightDeal.heroImage?.id && (
        <ImageContainer paletteType="default">
          <Image
            aspectRatio="3:2"
            id={flightDeal.heroImage.id}
            fit="center"
            dpr={2}
            height="800"
            width="600"
          />
          <LabelContainer>
            <UrgencyLabel variant="critical2">
              {intl.formatMessage(
                {
                  id: 'Zt5slAeA+G',
                  defaultMessage: 'OFFER ENDS IN {countdown}',
                  description: 'Urgency label showing time remaining for a flight deal',
                },
                { countdown: endDateDifference },
              )}
            </UrgencyLabel>
          </LabelContainer>
          {hasMultipleAirlines && hasCarrierLogo && (
            <CarrierContainer>
              <CarrierImage
                height={34}
                fit="center"
                image={flightDeal.carrier.logo}
                alt={`Logo of ${flightDeal.carrier.name}`}
              />
            </CarrierContainer>
          )}
        </ImageContainer>
      )}

      <Group direction="vertical" gap={8}>
        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="jI7BnDjCIN"
            defaultMessage="{name} {code}"
            description="Flight deal destination name and airport code"
            values={{
              name: flightDeal.destinationName,
              code: <BodyText variant="large" weight="bold" colour="neutral-three" as="span">{flightDeal.destinationAirportCode}</BodyText>,
            }}
          />
        </BodyText>

        <div>
          <PriceRowPriceCaption>{intl.formatMessage(
            {
              id: 'MSwHYA2Mdv',
              defaultMessage: '{fareClass} {fareType} from',
              description: 'Flight deals grid card price caption with fare class and fare type',
            },
            { fareClass: fareClassLabel, fareType: fareTypeLabel },
          )}
          </PriceRowPriceCaption>

          <PriceRowPrice
            size="L"
            price={flightDeal.perAdultPrice}
            saleUnit="adult"
          />
        </div>
      </Group>
    </FlightCardContainer>
  )
}

export default FlightsDealsGridCard
